<template>
  <v-dialog :value="dialog" max-width="600px" persistent>
    <v-card
        :class="{ 'grey lighten-2': dragover }"
        @drop.prevent="onDrop($event)"
        @dragover.prevent="dragover = true"
        @dragenter.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
    >
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <v-card-text>
        <v-row align="center" class="d-flex flex-column" dense justify="center">
          <v-icon :class="[dragover ? 'mt-2, mb-6' : 'mt-5']" color="primary " size="60">
            mdi-cloud-upload
          </v-icon>
          <p>
            Drop your {{ multiple ? "files" : "file" }} here, or click to select {{ multiple ? "them" : "it" }}.
          </p>
          <p class="">
            Expected file formats ({{expectedFileFormats}}), not greater than ({{fieldName === "Upload video" ? "10 MB" : "1 MB"}})
          </p>
          <v-btn :disabled="loading" class="mt-3" color="primary" small @click="chooseFile">
            Choose {{ multiple ? "Files" : "File" }}
          </v-btn>
          <input
              v-show="false"
              id="fileInput"
              ref="fileInput"
              :multiple="multiple"
              type="file"
              :accept='acceptedFileTypes'
              @change="onInput"
          />
        </v-row>
        <v-virtual-scroll
            v-if="uploadedFiles.length > 0"
            :items="uploadedFiles"
            height="200"
            item-height="50"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.name">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                  <span class="ml-3 text--secondary">
                    {{ (item.size / 1000000).toFixed(2) }} MB</span
                  >
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                    :disabled="loading"
                    color="primary"
                    icon
                    @click.stop="removeFile(item.name)">
                  <v-icon size="25"> mdi-close-circle</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-virtual-scroll>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn
            :disabled="loading"
            color="primary"
            text
            @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
            :disabled="!uploadedFiles.length || loading"
            color="primary"
            text
            @click.stop="submit"
        >
          <v-icon>mdi-upload</v-icon>
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapGetters} from "vuex";

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    fieldName: {
      type: String,
      required: true,
      default: ""
    },
    acceptedFileTypes: {
      type: String,
      default: ''
    },
    expectedFileFormats: {
      type: String,
      default: 'JPG, JPEG, PNG, PDF, DOC, PPT'
    }
  },
  data() {
    return {
      loading: false,
      dragover: false,
      uploadedFiles: [],
      inputFiles: [],
      postBody: new FormData()
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/GET_USER",
    }),
  },
  methods: {
    closeDialog() {
      this.uploadedFiles = [];
      this.$emit("onClose");
    },

    chooseFile() {
      document.getElementById('fileInput').click()
    },

    onInput() {
      this.inputFiles = this.$refs.fileInput.files
      if (!this.multiple) this.uploadedFiles = []
      if (this.inputFiles.length) {
        Array.from(this.inputFiles).forEach((file) => {
          if (this.fieldName === 'Upload video' && !file.type.startsWith('video')) {
            this.$snackbar.showMessage({
              content: "File selected is not a video",
              color: "info",
            })
            return;
          }
          if (file.size > (this.fieldName === 'Upload video' ? 10000000 : 1000000)) {
            this.$snackbar.showMessage({
              content: `File selected is greater than ${this.fieldName === 'Upload video' ? "10 MB" : "1 MB"}`,
              color: "info",
            })
          }
          else this.uploadedFiles.push(file)
        });
      }
    },

    onDrop(e) {
      this.dragover = false;
      this.inputFiles = e.dataTransfer.files
      if (!this.multiple) this.uploadedFiles = []
      if (this.inputFiles.length) {
        if (!this.multiple && this.inputFiles.length > 1) {
          this.$snackbar.showMessage({
            content: "More than one file was selected",
            color: "info",
          })
        } else {
          this.inputFiles.forEach((file) => {
            if (this.fieldName === 'Upload video' && !file.type.startsWith('video')) {
              this.$snackbar.showMessage({
                content: "File selected is not a video",
                color: "info",
              })
              return;
            }
            if (file.size > (this.fieldName === 'Upload video' ? 10000000 : 1000000)) {
              this.$snackbar.showMessage({
                content: `File selected is greater than ${this.fieldName === 'Upload video' ? "10 MB" : "1 MB"}`,
                color: "info",
              })
            }
            else this.uploadedFiles.push(file)
          });
        }
      }
    },

    removeFile(fileName) {
      const index = this.uploadedFiles.findIndex(file => file.name === fileName);
      if (index > -1) this.uploadedFiles.splice(index, 1);
    },

    async submit() {
      try {
        this.dragover = false;
        this.loading = true
        this.postBody.append(this.fieldName === 'Upload video' ? 'video_mentors' : this.fieldName, this.uploadedFiles[0]);
        const {data: res} = await this.axios.patch(`/profile/update_profile/${this.user.id}/`, this.postBody)
        this.$emit("onFilesUploaded", res)
        document.getElementById('fileInput').value = ""
        this.$snackbar.showMessage({
          content: this.multiple ? "Files were" : "File was" + " uploaded successfully",
          color: "success",
        });
        this.loading = false
        this.closeDialog()
      } catch (error) {
        this.loading = false;
        document.getElementById('fileInput').value = ""
        this.$snackbar.showMessage({
          content: error?.response?.data[this.fieldName][0] || this.$t('errors.somethingWentWrongError'),
          color: "error",
        });
      }
    }
  }
};
</script>
<style scoped>
.v-btn {
  text-transform: none !important;
}

.v-virtual-scroll::-webkit-scrollbar {
  width: 8px;
}

.v-virtual-scroll::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px rgb(255, 255, 255);
  border-radius: 0;
}

.v-virtual-scroll::-webkit-scrollbar-thumb {
  background: #b7bdc9;
  border-radius: 8px;
}

.v-virtual-scroll::-webkit-scrollbar-thumb:hover {
  background: #a3aab9;
}
</style>
